/**
 * Requirement: ticket 7056 - people who left have been marked by renaming the user ("Jane Doe-deleted",
 * "Jane Doe - Deleted"); deleted accounts count as left too (the store checks those). Their rows are
 * hidden, but their work stays in the totals: a total that shrank whenever someone left would rewrite
 * past months.
 */
export function isFormerStaffName(name: string | null | undefined): boolean {
  return /delete/i.test(name || '');
}

// Requirement: ticket 7056 (Oct 20 meeting) - the integration accounts that create leads and book
// estimates on their own. They keep their rows, so the volume they handle stays visible.
const BOT_EMAILS = [ 'dialpad@youmoveme.com', 'grace@avoca.ai' ];
// Karve and Move Right staff, who only appear on jobs while testing.
const STAFF_EMAIL_DOMAINS = [ 'karveit.ca', 'moveright.app' ];

const normalizeEmail = (email: string | null | undefined): string => (email || '').trim().toLowerCase();

/**
 * Requirement: ticket 7056 (Oct 21 meeting) - Karve / Move Right staff rows are hidden like people who
 * left; their work stays in the totals.
 */
export function isStaffEmail(email: string | null | undefined): boolean {
  return STAFF_EMAIL_DOMAINS.includes(normalizeEmail(email).split('@')[1]);
}

/**
 * Requirement: ticket 7056 (Oct 20 meeting) - the Mean row is the benchmark agents compare themselves
 * with, so bots and Karve / Move Right staff are left out of it. Their rows and their work in the
 * totals stay.
 */
export function isLeftOutOfMean(email: string | null | undefined): boolean {
  return BOT_EMAILS.includes(normalizeEmail(email)) || isStaffEmail(email);
}

/**
 * The note under the leaderboard table, explaining why the visible rows don't add up to the totals.
 * Unassigned jobs were never in the totals (they are nobody's work), but the number of leads nobody
 * owns is shown rather than buried. Empty when nothing is hidden.
 */
export function buildHiddenRowsNote(
  formerStaffCount: number,
  unassignedLeads = 0,
  role = 'Sales Agent',
  leftOutOfMeanCount = 0,
  hiddenStaffCount = 0,
): string {
  const parts: string[] = [];
  if (formerStaffCount) {
    const people = formerStaffCount === 1 ? '1 person who has left' : `${formerStaffCount} people who have left`;
    parts.push(`Totals include the work of ${people}; their rows are hidden.`);
  }
  if (hiddenStaffCount) {
    parts.push('Karve / Move Right staff rows are hidden; their work stays in the totals.');
  }
  if (unassignedLeads) {
    const leads = unassignedLeads === 1 ? '1 lead has' : `${unassignedLeads.toLocaleString('en-US')} leads have`;
    parts.push(`${leads} no ${role} and ${unassignedLeads === 1 ? 'is' : 'are'} not counted.`);
  }
  if (leftOutOfMeanCount) {
    parts.push('Mean excludes bots.');
  }
  return parts.join(' ');
}

const nameList = (names: string[]): string =>
  [ ...new Set(names.map((n) => n.trim()).filter(Boolean)) ].sort((a, b) => a.localeCompare(b)).join(', ');

/**
 * Requirement: ticket 7056 (MR review) - hovering the note names the people it counts, one line per
 * group. Empty when nobody is hidden or left out.
 */
export function buildHiddenRowsTooltip(groups: { left: string[]; staff: string[]; bots: string[] }): string {
  const lines: [ string, string ][] = [
    [ 'People who left', nameList(groups.left) ],
    [ 'Staff hidden', nameList(groups.staff) ],
    [ 'Left out of the mean', nameList(groups.bots) ],
  ];
  return lines.filter(([ , names ]) => names).map(([ label, names ]) => `${label}: ${names}`).join('\n');
}
